import Head from 'next/head';

type ImageType = {
  url: string;
  alt: string;
  dimensions: { width: number; height: number };
};

type SEOType = {
  meta_title: string;
  meta_description?: string;
  canonical?: string;
  meta_properties?: { name?: string; property?: string; content: string }[];
  og_title?: string;
  og_description?: string;
  og_image?: ImageType;
  structuredData?: string;
  meta_robots?: string;
};

const SEO = ({
  meta_title,
  meta_description,
  canonical,
  meta_properties,
  og_title,
  og_description,
  og_image,
  meta_robots,
  structuredData,
}: SEOType) => {
  const containsMetaProperties = (key) =>
    !!meta_properties?.some((meta) => meta?.name === key || meta?.property === key);

  const ogTitle = og_title || meta_title;
  const ogDescription = og_description || meta_description;

  return (
    <Head>
      {/* Meta Data */}
      {meta_title && <title>{meta_title}</title>}
      {!containsMetaProperties('description') && meta_description && (
        <meta name="description" content={meta_description} />
      )}

      {/* OG Data */}
      {!containsMetaProperties('og:title') && ogTitle && (
        <meta property="og:title" content={ogTitle} />
      )}
      {!containsMetaProperties('og:description') && ogDescription && (
        <meta property="og:description" content={ogDescription} />
      )}
      {og_image?.url && <meta property="og:image" content={og_image.url} />}

      {/* Twitter Data */}
      {!containsMetaProperties('twitter:card') && (
        <meta name="twitter:card" content="summary_large_image" />
      )}
      {!containsMetaProperties('twitter:title') && ogTitle && (
        <meta name="twitter:title" content={ogTitle} />
      )}
      {!containsMetaProperties('twitter:description') && ogDescription && (
        <meta name="twitter:description" content={ogDescription} />
      )}
      {!containsMetaProperties('twitter:site') && (
        <meta name="twitter:site" content="https://twitter.com/virginexp" />
      )}
      {!containsMetaProperties('twitter:creator') && (
        <meta name="twitter:creator" content="@virginexp" />
      )}
      {!containsMetaProperties('twitter:image') && og_image?.url && (
        <meta name="twitter:image" content={og_image.url} />
      )}

      {/* Other Properties */}
      {meta_properties &&
        meta_properties.map(
          ({ name = null, property = null, content }) =>
            (name && property && (
              <meta key={name} name={name} property={property} content={content} />
            )) ||
            (name && <meta key={name} name={name} content={content} />) ||
            (property && <meta key={property} property={property} content={content} />)
        )}

      {/* with key to be sure there is only one tag rendered per page */}
      {meta_robots && <meta name="robots" key="robots" content={meta_robots} />}
      {canonical ? <link rel="canonical" href={canonical} /> : null}

      {structuredData && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} />
      )}
    </Head>
  );
};

export default SEO;
